<template>
  <section class="invoice-add-wrapper">
    <validation-observer ref="simpleRules">
      <b-form>
        <b-row>
          <!-- ============================================ -->
          <!-- PANEL IZQUIERDO — Datos de la cotización -->
          <!-- ============================================ -->
          <b-col
            cols="12"
            md="5"
            xl="5"
          >
            <b-card
              class="quote-add-wrapper"
              no-body
            >
              <b-card-body>
                <!-- Badge de estado -->
                <b-row>
                  <b-col
                    cols="12"
                    md="11"
                  >
                    <h3>
                      <b-badge
                        variant="warning"
                        pill
                      >
                        #{{ customerQuoteData.Id }} - {{ customerQuoteData.Estado }}
                      </b-badge>
                    </h3>
                  </b-col>
                </b-row>

                <!-- Info de la cotización (read-only) -->
                <b-row class="mt-1">
                  <b-col cols="12">
                    <p class="mb-1">
                      <span class="font-weight-bold">Colocada por:</span>
                      {{ customerQuoteData.UsuarioNombre }} <b>el:</b> {{ customerQuoteData.FechaIngresoSistema }}
                      <br>
                      <span class="font-weight-bold">Fecha de la cotización:</span>
                      {{ customerQuoteData.FechaCotizacionCliente }}
                      <br>
                      <span class="font-weight-bold">Válida hasta:</span>
                      {{ customerQuoteData.FechaVencimiento }}
                    </p>
                  </b-col>
                </b-row>

                <!-- Cliente (read-only) -->
                <div v-if="customerData.id">
                  <h5 class="mb-50">
                    #{{ customerData.id }} - {{ customerData.name }}
                  </h5>
                  <p class="mb-1">
                    <span class="font-weight-bold">NIT: </span>
                    {{ (customerData.nit_cf === 'Y') ? `CF - ${customerData.nit}` : customerData.nit }}
                    <br>
                    <span class="font-weight-bold">Cel: </span>{{ customerData.cellphone || '-' }}
                    <br>
                    <span class="font-weight-bold">Tel: </span>{{ customerData.phone || '-' }}
                    <br>
                    <span class="font-weight-bold">Lista de precios: </span>
                    {{ (customerQuoteData.PriceListInfo && customerQuoteData.PriceListInfo.name) ? customerQuoteData.PriceListInfo.name : 'No asignada' }}
                  </p>

                  <!-- Dirección -->
                  <div class="mb-1">
                    <span class="font-weight-bold">Dirección: </span>
                    <p class="ml-1 mb-50">
                      {{ customerQuoteData.shipping_address_text }}
                    </p>
                    <div class="ml-1">
                      <a
                        href="#"
                        @click.prevent="showAddressSelector = true"
                      >+ Ver más direcciones</a>
                    </div>
                  </div>
                </div>

                <!-- Moneda y tipo de venta -->
                <b-row>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group>
                      <h6>
                        <b>Moneda:</b>
                      </h6>
                      <v-select
                        v-model="customerQuoteData.currency_id"
                        :options="currencies"
                        label="name"
                        :reduce="currency => currency.id"
                        placeholder="Moneda"
                      >
                        <template #option="option">
                          {{ option.name }} {{ option.symbol }}
                        </template>
                      </v-select>
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group>
                      <h6>
                        <b>Tipo de venta:</b>
                      </h6>
                      <b-form-select
                        v-model="customerQuoteData.origin_sale"
                        :options="saleOriginOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Observaciones -->
                <b-form-group>
                  <h6>
                    <b>Observaciones:</b>
                  </h6>
                  <b-form-textarea
                    v-model="customerQuoteData.observations"
                    placeholder="Comentarios de la cotización"
                    rows="2"
                  />
                </b-form-group>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- ============================================ -->
          <!-- PANEL DERECHO — Catálogo + Grilla -->
          <!-- ============================================ -->
          <b-col
            v-if="customerData.id"
            cols="12"
            md="7"
            xl="7"
          >
            <!-- Catálogo -->
            <b-card no-body>
              <b-card-body class="pb-50">
                <h6 class="mb-1">
                  <b>Catálogo de productos:</b>
                </h6>

                <b-form-input
                  v-model="searchQuery"
                  placeholder="Buscar por código o nombre de estilo..."
                  class="mb-1"
                />

                <div
                  v-if="!customerQuoteData.price_list_id"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-warning mb-50"
                  />
                  <p class="text-muted mb-0">
                    La cotización no tiene lista de precios asignada
                  </p>
                </div>

                <div
                  v-else-if="!searchQuery.trim() && estilos.length === 0"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    Busca un producto por código o nombre para agregarlo
                  </p>
                </div>

                <div
                  v-else-if="isLoadingProducts"
                  class="text-center py-2"
                >
                  <b-spinner
                    variant="primary"
                    class="mb-50"
                  />
                  <p class="text-muted mb-0">
                    Buscando productos...
                  </p>
                </div>

                <div
                  v-else-if="estilos.length === 0 && searchQuery.trim().length >= 2"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    No se encontraron productos para "{{ searchQuery }}"
                  </p>
                </div>

                <div
                  v-else-if="estilos.length > 0"
                  class="quote-add-wrapper__catalogo"
                >
                  <div
                    v-for="estilo in estilos"
                    :key="estilo.code + '-' + estilo.color"
                    class="estilo-card"
                    :class="{ 'estilo-card--added': isStyleAdded(estilo.code, estilo.color) }"
                    @click="onToggleStyle(estilo)"
                  >
                    <img
                      :src="getEstiloThumbnail(estilo)"
                      :alt="estilo.code"
                      class="estilo-card__thumb"
                      @error="onEstiloImageError"
                    >
                    <div class="estilo-card__info">
                      <span class="estilo-card__code">
                        {{ estilo.code }}
                      </span>
                      <small class="text-muted">
                        {{ estilo.color }}
                      </small>
                    </div>
                    <div class="estilo-card__action">
                      <feather-icon
                        v-if="isStyleAdded(estilo.code, estilo.color)"
                        icon="CheckCircleIcon"
                        size="20"
                        class="text-success"
                      />
                      <feather-icon
                        v-else
                        icon="PlusCircleIcon"
                        size="20"
                        class="text-primary"
                      />
                    </div>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Grilla de la cotización -->
            <b-card no-body>
              <b-card-body>
                <h6 class="mb-1">
                  <b>Detalle de la cotización ({{ activeLines.length }} productos):</b>
                </h6>

                <div class="order-grid">
                  <div
                    v-if="gridRows.length === 0"
                    class="text-center py-2"
                  >
                    <feather-icon
                      icon="ShoppingCartIcon"
                      size="32"
                      class="text-muted mb-1"
                    />
                    <p class="text-muted mb-0">
                      La cotización no tiene productos
                    </p>
                  </div>

                  <div
                    v-else
                    class="table-responsive"
                  >
                    <table class="table table-sm order-grid__table">
                      <thead>
                        <tr>
                          <th class="order-grid__th-style">
                            Estilo-Color
                          </th>
                          <th
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__th-size"
                          >
                            {{ size }}
                          </th>
                          <th class="order-grid__th-num">
                            TOTAL
                          </th>
                          <th class="order-grid__th-price">
                            Precio unit
                          </th>
                          <th class="order-grid__th-num">
                            MONEDA
                          </th>
                          <th class="order-grid__th-amount">
                            TOTAL EN {{ currencySymbol }}
                          </th>
                          <th class="order-grid__th-actions" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in gridRows"
                          :key="row.key"
                        >
                          <td class="order-grid__style-cell">
                            <span class="font-weight-bold">
                              {{ row.estilo }}
                            </span>
                            <small class="text-muted d-block">
                              {{ row.color }}
                            </small>
                          </td>
                          <td
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__qty-cell"
                          >
                            <template v-if="row.sizeMap[size]">
                              <!-- Venta caída: la cantidad solicitada no se limita al stock; el faltante se muestra, no se bloquea -->
                              <input
                                type="number"
                                class="order-grid__qty-input"
                                :class="{
                                  'order-grid__qty-input--shortfall': shortfallOf(row.sizeMap[size]) > 0,
                                  'order-grid__qty-input--empty': row.sizeMap[size].cantidad === 0,
                                }"
                                :value="row.sizeMap[size].cantidad || ''"
                                :title="shortfallOf(row.sizeMap[size]) > 0 ? `Faltan ${shortfallOf(row.sizeMap[size])} (disponible ${row.sizeMap[size].stock})` : (row.sizeMap[size].stock !== null ? `Disponible ${row.sizeMap[size].stock}` : '')"
                                placeholder="0"
                                min="0"
                                @change="onQtyChange(row.sizeMap[size], $event)"
                              >
                              <small
                                v-if="row.sizeMap[size].stock !== null"
                                class="order-grid__stock-hint"
                                :class="{ 'order-grid__stock-hint--shortfall': shortfallOf(row.sizeMap[size]) > 0 }"
                              >
                                {{ shortfallOf(row.sizeMap[size]) > 0 ? `Faltan ${shortfallOf(row.sizeMap[size])}` : row.sizeMap[size].stock }}
                              </small>
                              <!-- Talla con oferta: precio de lista tachado y precio ofertado. -->
                              <div
                                v-if="lineOffer(row.sizeMap[size])"
                                class="order-grid__offer"
                                :title="`Oferta: ahorra ${currencySymbol}${lineOffer(row.sizeMap[size]).ahorro.toFixed(2)} por unidad`"
                              >
                                <s>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).lista.toFixed(2) }}</s>
                                <strong>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).oferta.toFixed(2) }}</strong>
                              </div>
                            </template>
                          </td>
                          <td class="text-center font-weight-bold">
                            {{ row.totalQty }}
                          </td>
                          <td>
                            <div class="price-input">
                              <span class="price-input__display price-input__display--locked">
                                {{ currencySymbol }}{{ Number(row.precioActual).toFixed(2) }}
                              </span>
                              <span
                                v-if="row.isCustomPrice"
                                class="price-input__badge"
                                title="Precio propio de la lista; no sigue al precio base del estilo"
                              >
                                lista
                              </span>
                              <span
                                v-else
                                class="price-input__original"
                                title="Esta variante sigue el precio base del estilo"
                              >
                                base
                              </span>
                            </div>
                          </td>
                          <td class="text-center text-muted">
                            {{ currencyCode }}
                          </td>
                          <td class="text-right font-weight-bold order-grid__row-total">
                            {{ currencySymbol }}{{ row.totalAmount }}
                          </td>
                          <td class="text-center">
                            <button
                              type="button"
                              class="btn btn-sm btn-flat-danger"
                              @click="onRemoveStyle(row.key)"
                            >
                              <feather-icon
                                icon="TrashIcon"
                                size="14"
                              />
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div class="order-grid__footer">
                      <span
                        v-if="totalShortfall > 0"
                        class="order-grid__shortfall-total"
                      >
                        Faltante por stock: {{ totalShortfall }} unidades
                      </span>
                      <span>Total:</span>
                      <span class="order-grid__grand-total">
                        {{ currencySymbol }}{{ grandTotal }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Minimo de unidades de la lista (PRECIO VOLUMEN); el servidor tambien bloquea. -->
                <price-list-unit-rule-notice
                  :customer-id="customerQuoteData.customer_id"
                  :price-list-id="customerQuoteData.price_list_id"
                  :units="unitRuleUnits"
                  documento="cotizacion"
                  @change="unitRuleOk = $event"
                />

                <b-button
                  v-ripple.400="'rgba(255, 159, 67, 0.15)'"
                  variant="warning"
                  block
                  class="mt-1"
                  :disabled="isSavingQuote || activeLines.length === 0 || !unitRuleOk"
                  @click="updateData"
                >
                  <template v-if="isSavingQuote">
                    <b-spinner
                      small
                      class="mr-50"
                    />
                    Actualizando...
                  </template>
                  <template v-else>
                    Actualizar cotización
                  </template>
                </b-button>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <customers-address-selector
          :show.sync="showAddressSelector"
          :customer-id="customerQuoteData.customer_id"
          :selected-address-id="customerQuoteData.shipping_address_id"
          @address-selected="selectAddress"
        />
      </b-form>
    </validation-observer>
  </section>
</template>

<script>
import axios from '@axios'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import { lineOffer, rowPriceSummary, catalogVariant } from '../components/gridOffer'
import { coveredUnits } from '../components/unitRuleStatus'
import PriceListUnitRuleNotice from '../components/PriceListUnitRuleNotice.vue'
import CustomersAddressSelector from '../../contacts/customers/CustomersAddressSelector.vue'

export default {
  components: {
    PriceListUnitRuleNotice,
    vSelect,
    ValidationObserver,
    CustomersAddressSelector,
  },
  directives: {
    Ripple,
  },

  props: {
    quoteId: {
      type: Number,
      default: () => 0,
    },
  },

  data() {
    return {
      customerQuoteData: {
        Id: 0,
        Estado: '',
        FechaIngresoSistema: '',
        FechaCotizacionCliente: '',
        FechaVencimiento: '',
        UsuarioNombre: '',
        customer_id: 0,
        shipping_address_id: null,
        shipping_address_text: '',
        origin_sale: 'Cliente Final',
        observations: '',
        currency_id: null,
        price_list_id: null,
        PriceListInfo: null,
        CurrencyInfo: null,
      },

      customerData: {},
      currencies: [],

      allVariants: [],
      estilos: [],
      searchQuery: '',
      isLoadingProducts: false,

      orderLines: [],

      saleOriginOptions: [
        { value: 'Cliente Final', text: 'Cliente Final' },
        { value: 'Venta en línea', text: 'Venta en línea' },
      ],

      showAddressSelector: false,

      isSavingQuote: false,
      // Lo informa PriceListUnitRuleNotice: false bloquea el boton de guardar.
      unitRuleOk: true,
      timeout: null,

    }
  },

  computed: {
    // Unidades para el minimo de la lista: en cotizacion, las cubiertas por
    // stock; en pedido, las del pedido (ver unitRuleStatus.js).
    unitRuleUnits() {
      return coveredUnits(this.activeLines)
    },

    activeLines() {
      return this.orderLines.filter(line => line.cantidad > 0)
    },

    currencySymbol() {
      if (!this.customerQuoteData.currency_id) return 'Q'
      const selected = this.currencies.find(c => c.id === this.customerQuoteData.currency_id)
      return selected ? (selected.symbol || 'Q') : 'Q'
    },

    currencyCode() {
      if (!this.customerQuoteData.currency_id) return 'GTQ'
      const selected = this.currencies.find(c => c.id === this.customerQuoteData.currency_id)
      return selected && selected.code ? selected.code : 'GTQ'
    },

    allSizes() {
      const sizes = new Set()
      this.orderLines.forEach(line => sizes.add(line.size))
      const arr = [...sizes]
      arr.sort((a, b) => {
        const numA = parseFloat(a)
        const numB = parseFloat(b)
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
        return a.localeCompare(b)
      })
      return arr
    },

    gridRows() {
      const groups = {}

      this.orderLines.forEach(line => {
        const key = `${line.estilo}-${line.color}`
        if (!groups[key]) {
          groups[key] = {
            key,
            estilo: line.estilo,
            color: line.color,
            precioActual: line.precioActual,
            precioBase: line.precioBase,
            isCustomPrice: line.isCustomPrice,
            sizeMap: {},
            lines: [],
          }
        }
        groups[key].sizeMap[line.size] = line
        groups[key].lines.push(line)
      })

      return Object.values(groups).map(group => ({
        ...group,
        ...rowPriceSummary(group.lines),
        totalQty: group.lines.reduce((sum, l) => sum + l.cantidad, 0),
        totalAmount: Number(
          group.lines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
        ).toFixed(2),
      }))
    },

    grandTotal() {
      return Number(
        this.orderLines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
      ).toFixed(2)
    },

    totalShortfall() {
      return this.orderLines.reduce((sum, l) => sum + this.shortfallOf(l), 0)
    },
  },

  watch: {
    searchQuery(val) {
      if (this.timeout) clearTimeout(this.timeout)
      const query = val.trim()
      if (query.length < 2) {
        if (query.length === 0) {
          this.allVariants = []
          this.estilos = []
        }
        return
      }
      this.timeout = setTimeout(() => {
        this.searchProductsFromServer()
      }, 400)
    },
  },

  mounted() {
    if (this.quoteId > 0) {
      this.getCustomerQuoteData()
      this.getCurrencies()
    }
  },

  methods: {
    lineOffer,

    isStyleAdded(code, color) {
      return this.orderLines.some(l => l.estilo === code && l.color === color)
    },

    getCustomerQuoteData() {
      axios
        .get(`customer-quote/${this.quoteId}`)
        .then(response => {
          const d = response.data.data
          this.customerQuoteData.Id = d.Id
          this.customerQuoteData.Estado = d.Estado
          this.customerQuoteData.FechaIngresoSistema = d.FechaIngresoSistema
          this.customerQuoteData.FechaCotizacionCliente = d.FechaCotizacionCliente
          this.customerQuoteData.FechaVencimiento = d.FechaVencimiento
          this.customerQuoteData.UsuarioNombre = d.UsuarioNombre
          this.customerQuoteData.observations = d.Observaciones || ''
          this.customerQuoteData.origin_sale = d.OriginSale || 'Cliente Final'
          this.customerQuoteData.shipping_address_id = d.DireccionEnvioId
          this.customerQuoteData.shipping_address_text = d.DireccionEnvio
          this.customerQuoteData.PriceListInfo = d.PriceListInfo
          this.customerQuoteData.price_list_id = d.PriceListInfo ? d.PriceListInfo.id : null
          this.customerQuoteData.currency_id = d.CurrencyId
          this.customerQuoteData.CurrencyInfo = d.CurrencyInfo

          this.customerData = d.CustomerInfo || {}
          this.customerQuoteData.customer_id = d.CustomerInfo ? d.CustomerInfo.id : 0

          this.orderLines = []
          d.Detalle.forEach(el => {
            const basePrice = parseFloat(el.precio_original)
            const currentPrice = parseFloat(el.precio_cotizacion)
            const listPrice = el.precio_lista !== undefined ? parseFloat(el.precio_lista) : currentPrice
            this.orderLines.push({
              sku: el.sku,
              estilo: el.producto,
              color: el.producto_color,
              descripcion: el.producto_desc,
              precioBase: basePrice,
              precioActual: currentPrice,
              cantidad: el.cantidad,
              cantidadOriginal: el.cantidad,
              productVariantId: el.idproductos,
              presentacion: el.producto_presentacion,
              size: el.producto_talla,
              imagen: el.producto_imagen,
              isCustomPrice: basePrice !== currentPrice,
              precioLista: listPrice,
              isOffer: listPrice > currentPrice,
              stock: null,
            })
          })

          this.loadAllSizesForOrderStyles()
        })
        .catch(error => { this.showErrors(error) })
    },

    loadAllSizesForOrderStyles() {
      if (!this.customerQuoteData.price_list_id || !this.customerQuoteData.customer_id) return
      if (this.orderLines.length === 0) return

      const styleMap = {}
      this.orderLines.forEach(l => {
        const key = `${l.estilo}-${l.color}`
        if (!styleMap[key]) {
          styleMap[key] = {
            estilo: l.estilo,
            color: l.color,
            precioActual: l.precioActual,
            precioBase: l.precioBase,
          }
        }
      })

      const styles = Object.values(styleMap)

      Promise.all(styles.map(s => axios.get('product-variant-with-prices', {
        params: {
          price_list_id: this.customerQuoteData.price_list_id,
          customer_id: this.customerQuoteData.customer_id,
          search: s.estilo,
          perPage: 100,
        },
      })))
        .then(responses => {
          responses.forEach((resp, idx) => {
            const s = styles[idx]
            const variants = resp.data.data.data || []
            variants.forEach(v => {
              if (v.code !== s.estilo) return
              if (v.color !== s.color) return

              const existingIdx = this.orderLines.findIndex(l => l.sku === v.sku)
              if (existingIdx !== -1) {
                this.$set(this.orderLines, existingIdx, {
                  ...this.orderLines[existingIdx],
                  stock: v.stock,
                  categoria: v.categoria,
                })
                return
              }

              this.orderLines.push({
                sku: v.sku,
                estilo: s.estilo,
                color: s.color,
                descripcion: v.descripcion || '',
                precioBase: v.base_price,
                precioActual: v.precio,
                precioLista: v.precio_lista,
                isOffer: v.is_offer,
                cantidad: 0,
                cantidadOriginal: 0,
                productVariantId: v.id,
                presentacion: v.presentacion,
                size: v.size,
                imagen: v.imagen,
                isCustomPrice: v.is_custom_price,
                stock: v.stock,
                categoria: v.categoria,
              })
            })
          })
        })
        .catch(error => { this.showErrors(error) })
    },

    getCurrencies() {
      axios.get('currency-active')
        .then(response => { this.currencies = response.data.data })
        .catch(error => { this.showErrors(error) })
    },

    searchProductsFromServer() {
      if (!this.customerQuoteData.price_list_id || !this.customerQuoteData.customer_id) return

      const query = this.searchQuery.trim()
      if (query.length < 2) return

      this.isLoadingProducts = true

      const params = {
        price_list_id: this.customerQuoteData.price_list_id,
        customer_id: this.customerQuoteData.customer_id,
        search: query,
        perPage: 50,
      }

      axios.get('product-variant-with-prices', { params })
        .then(response => {
          this.allVariants = response.data.data.data || []
          this.groupByEstilo()
          this.isLoadingProducts = false
        })
        .catch(error => {
          this.isLoadingProducts = false
          this.showErrors(error)
        })
    },

    groupByEstilo() {
      const map = {}

      this.allVariants.forEach(v => {
        const code = v.code || v.label.split('-').slice(0, -1).join('-') || v.label
        const key = `${code}-${v.color}`

        if (!map[key]) {
          map[key] = {
            code,
            description: v.descripcion || '',
            color: v.color || '',
            image: v.imagen || null,
            variants: [],
          }
        }

        map[key].variants.push(catalogVariant(v))
      })

      Object.values(map).forEach(estilo => {
        estilo.variants.sort((a, b) => {
          const numA = parseFloat(a.size)
          const numB = parseFloat(b.size)
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
          return a.size.localeCompare(b.size)
        })
      })

      this.estilos = Object.values(map)
    },

    onToggleStyle(estilo) {
      // Cada talla con su propio precio (lista + oferta vigente); ya no se
      // hereda el precio de otra talla del mismo estilo.
      estilo.variants.forEach(v => {
        const exists = this.orderLines.some(l => l.sku === v.sku)
        if (exists) return

        const precioActual = v.precio
        this.orderLines.push({
          sku: v.sku,
          estilo: estilo.code,
          color: estilo.color,
          descripcion: v.descripcion || estilo.description,
          precioBase: v.base_price,
          precioActual,
          cantidad: 0,
          cantidadOriginal: 0,
          productVariantId: v.id,
          presentacion: v.presentacion,
          size: v.size,
          imagen: estilo.image,
          isCustomPrice: v.is_custom_price,
          precioLista: v.precio_lista,
          isOffer: v.is_offer,
          stock: v.stock,
          categoria: v.categoria,
        })
      })
    },

    onRemoveStyle(styleKey) {
      this.orderLines = this.orderLines.filter(
        line => `${line.estilo}-${line.color}` !== styleKey,
      )
    },

    onQtyChange(line, event) {
      const { target } = event
      const val = parseInt(target.value, 10)
      let qty
      if (Number.isNaN(val) || val < 0) {
        target.value = 0
        qty = 0
      } else {
        // Sin tope por stock: la cotización registra la cantidad solicitada por el cliente
        qty = val
        target.value = qty || ''
      }
      const stored = this.orderLines.find(l => l.sku === line.sku)
      if (stored) stored.cantidad = qty
    },

    shortfallOf(line) {
      if (!line || line.stock === null || line.stock === undefined) return 0
      return Math.max(0, (line.cantidad || 0) - line.stock)
    },

    getEstiloThumbnail(estilo) {
      if (estilo.image && estilo.image.path) {
        return buildStorageUrl(estilo.image.path)
      }
      return NO_IMAGE_URL
    },

    onEstiloImageError(e) {
      e.target.src = NO_IMAGE_URL
    },

    selectAddress(address) {
      if (!address) return
      this.customerQuoteData.shipping_address_id = address.id
      this.customerQuoteData.shipping_address_text = `${address.name}: ${address.address} - ${address.municipality} / ${address.department} ${address.reference ? `/ Referencia: ${address.reference}` : ''}`
    },

    updateData() {
      if (this.activeLines.length === 0) {
        this.makeToast('warning', 'Alerta', 'La cotización debe tener al menos un producto con cantidad mayor a 0.')
        return
      }

      if (!this.customerQuoteData.currency_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar una moneda.')
        return
      }

      this.$bvModal
        .msgBoxConfirm('¿Deseas actualizar la cotización?', {
          title: 'Confirmar',
          size: 'sm',
          okVariant: 'warning',
          cancelVariant: 'outline-dark',
          okTitle: 'SI',
          cancelTitle: 'NO',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (!value) return

          this.isSavingQuote = true

          const payload = {
            Id: this.customerQuoteData.Id,
            Estado: this.customerQuoteData.Estado,
            customer_id: this.customerQuoteData.customer_id,
            shipping_address_id: this.customerQuoteData.shipping_address_id,
            shipping_address_text: this.customerQuoteData.shipping_address_text,
            origin_sale: this.customerQuoteData.origin_sale,
            observations: this.customerQuoteData.observations,
            currency_id: this.customerQuoteData.currency_id,
            products: this.activeLines.map(line => ({
              product_id: line.productVariantId,
              label: line.estilo,
              color: line.color,
              presentacion: line.presentacion,
              size: line.size,
              description: line.descripcion,
              price: line.precioActual,
              quantity: line.cantidad,
              imagen: line.imagen,
            })),
          }

          axios
            .put(`customer-quote/${this.quoteId}`, payload)
            .then(async response => {
              if (response.data.res) {
                this.makeToast('success', 'Cotización actualizada', `Se ha actualizado la cotización #${response.data.data.Id} correctamente.`)
                this.isSavingQuote = false
                await new Promise(resolve => { window.setTimeout(resolve, 500) })
                this.$emit('addQuoteTab', parseInt(response.data.data.Id, 10))
              } else {
                throw response
              }
            })
            .catch(error => {
              this.isSavingQuote = false
              this.showErrors(error)
            })
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.quote-add-wrapper {
  .add-new-customer-header {
    padding: $options-padding-y $options-padding-x;
    color: $success;

    &:hover {
      background-color: rgba($success, 0.12);
    }
  }

  &__catalogo {
    max-height: 300px;
    overflow-y: auto;
    padding-right: 4px;
  }
}

.estilo-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fafafa;
  }

  &--added {
    border-color: #28c76f;
    background: #f0faf4;
  }

  &__thumb {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid #d8dee9;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__code {
    font-weight: 700;
    font-size: 13px;
    color: #1a3a5c;
  }

  &__action {
    flex-shrink: 0;
  }
}

.price-input {
  display: flex;
  align-items: center;
  gap: 6px;

  &__display {
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;

    &--locked {
      cursor: default;
      color: #1a3a5c;
    }
  }

  &__original {
    font-size: 10px;
    color: #b9b9c3;
    text-transform: uppercase;
  }

  &__badge {
    font-size: 10px;
    background: #fff3e8;
    color: #ff9f43;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 500;
  }

  &__field {
    width: 90px;
    padding: 2px 6px;
    border: 1px solid #1a3a5c;
    border-radius: 4px;
    font-size: 14px;
    outline: none;

    &:focus {
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.2);
    }
  }
}

.order-grid {
  &__table {
    font-size: 12px;
    white-space: nowrap;
    margin-bottom: 0;

    th {
      font-size: 10px;
      text-transform: uppercase;
      color: #6e6b7b;
      font-weight: 600;
      padding: 6px 4px;
      text-align: center;
      background: #f8f9fa;
      border-bottom: 2px solid #1a3a5c;
    }

    td {
      vertical-align: middle;
      padding: 4px;
      border-bottom: 1px solid #ebe9f1;
    }
  }

  &__th-style {
    min-width: 120px;
    text-align: left !important;
  }

  &__th-size {
    min-width: 48px;
  }

  &__th-num {
    min-width: 55px;
  }

  &__th-price {
    min-width: 110px;
  }

  &__th-amount {
    min-width: 110px;
    text-align: right !important;
  }

  &__th-actions {
    width: 40px;
  }

  &__style-cell {
    background: #fafbfc;
    border-right: 1px solid #ebe9f1;
    padding-left: 8px !important;
  }

  &__qty-cell {
    text-align: center;
  }

  &__qty-input {
    width: 40px;
    height: 28px;
    text-align: center;
    border: 1px solid #d8dee9;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 0;
    background: #fff;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus {
      outline: none;
      border-color: #1a3a5c;
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.15);
    }

    &--empty {
      background: #fafbfc;
      color: #b9b9c3;
      border-style: dashed;
    }

    &--shortfall {
      border-color: #ff9f43;
      background: #fff8f0;
      color: #b35c00;
    }
  }

  // Talla con oferta: precio de lista tachado y ofertado debajo de la cantidad.
  &__offer {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
    margin-top: 2px;
    font-size: 0.75rem;

    s {
      color: #82868b;
    }

    strong {
      color: #ea5455;
    }
  }

  &__stock-hint {
    display: block;
    font-size: 9px;
    color: #b9b9c3;
    margin-top: 1px;

    &--shortfall {
      color: #ff9f43;
      font-weight: 600;
    }
  }

  &__shortfall-total {
    margin-right: auto;
    font-size: 13px;
    font-weight: 600;
    color: #ff9f43;
  }

  &__row-total {
    white-space: nowrap;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 12px 8px;
    border-top: 2px solid #1a3a5c;
    font-size: 16px;
    font-weight: 600;
  }

  &__grand-total {
    color: #1a3a5c;
    font-size: 20px;
  }
}
</style>
